// src/features/account/components/ApiKeysList.tsx
import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  TextField,
  Chip,
  Alert,
  Snackbar,
  Tooltip,
} from '@mui/material';
import {
  Delete as DeleteIcon,
  Add as AddIcon,
  ContentCopy as CopyIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
} from '@mui/icons-material';
import { format } from 'date-fns';
import { fetchApiKeys, createApiKey, deleteApiKey } from '../api/accountApi';
import useAccountStore from '../../../store/accountStore';

const ApiKeysList: React.FC = () => {
  const { apiKeys = [], isLoading, error } = useAccountStore();
  
  // Local state
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [keyToDelete, setKeyToDelete] = useState<string | null>(null);
  const [newKeyName, setNewKeyName] = useState('');
  const [newKeyValue, setNewKeyValue] = useState<string | null>(null);
  const [showNewKey, setShowNewKey] = useState(true);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [fetchError, setFetchError] = useState<string | null>(null);

  // Fetch API keys on component mount
  useEffect(() => {
    const loadApiKeys = async () => {
      try {
        console.log('Fetching API keys...');
        await fetchApiKeys();
        console.log('API keys fetched:', useAccountStore.getState().apiKeys);
      } catch (err: any) {
        console.error('Error fetching API keys:', err);
        setFetchError(err.message || 'Failed to fetch API keys');
      }
    };
    
    loadApiKeys();
  }, []);

  // Copy key to clipboard
  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text).then(
      () => {
        setSnackbarMessage('API Key copied to clipboard');
        setSnackbarOpen(true);
      },
      () => {
        setSnackbarMessage('Failed to copy to clipboard');
        setSnackbarOpen(true);
      }
    );
  };

  // Handle creating a new API key
  const handleCreateKey = async () => {
    if (!newKeyName.trim()) return;
    
    try {
      const newKey = await createApiKey(newKeyName);
      setNewKeyValue(newKey.key);
      setNewKeyName('');
      setCreateDialogOpen(false);
    } catch (err: any) {
      console.error('Error creating API key:', err);
      setSnackbarMessage(err.message || 'Failed to create API key');
      setSnackbarOpen(true);
    }
  };

  // Handle deleting an API key
  const handleDeleteKey = async () => {
    if (!keyToDelete) return;
    
    try {
      await deleteApiKey(keyToDelete);
      setKeyToDelete(null);
      setDeleteDialogOpen(false);
    } catch (err: any) {
      console.error('Error deleting API key:', err);
      setSnackbarMessage(err.message || 'Failed to delete API key');
      setSnackbarOpen(true);
    }
  };

  // Close new key dialog and reset state
  const handleNewKeyDialogClose = () => {
    setNewKeyValue(null);
  };

  // Safely render the API keys list
  const renderApiKeysList = () => {
    // Ensure apiKeys is defined and is an array
    if (!apiKeys || !Array.isArray(apiKeys)) {
      return (
        <TableRow>
          <TableCell colSpan={5} align="center">
            <Typography variant="body1" py={3}>
              Failed to load API keys. Please try refreshing the page.
            </Typography>
          </TableCell>
        </TableRow>
      );
    }

    // Check if array is empty
    if (apiKeys.length === 0) {
      return (
        <TableRow>
          <TableCell colSpan={5} align="center">
            <Typography variant="body1" py={3}>
              You don't have any API keys yet. Create one to get started.
            </Typography>
          </TableCell>
        </TableRow>
      );
    }

    // Render API keys
    return apiKeys.map((key) => {
      // Make sure the key is valid
      if (!key || typeof key !== 'object') {
        return null;
      }

      return (
        <TableRow key={key.id || `unknown-${Math.random()}`}>
          <TableCell>{key.name || 'Unnamed Key'}</TableCell>
          <TableCell>
            <Box display="flex" alignItems="center">
              <Typography variant="body2" fontFamily="monospace">
                {/* Always show masked key for existing API keys */}
                {'•'.repeat(40)}
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                (Hidden for security)
              </Typography>
            </Box>
          </TableCell>
          <TableCell>
            {key.createdAt ? format(new Date(key.createdAt), 'MMM dd, yyyy') : 'Unknown date'}
          </TableCell>
          <TableCell>
            {key.lastUsed 
              ? format(new Date(key.lastUsed), 'MMM dd, yyyy HH:mm')
              : 'Never used'}
          </TableCell>
          <TableCell align="right">
            <IconButton
              color="error"
              onClick={() => {
                setKeyToDelete(key.id || null);
                setDeleteDialogOpen(true);
              }}
              disabled={!key.id}
            >
              <DeleteIcon />
            </IconButton>
          </TableCell>
        </TableRow>
      );
    });
  };

  // Handle refresh
  const handleRefresh = async () => {
    try {
      await fetchApiKeys();
      setFetchError(null);
    } catch (err: any) {
      console.error('Error refreshing API keys:', err);
      setFetchError(err.message || 'Failed to refresh API keys');
    }
  };

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={4}>
        <Typography variant="h4" component="h1">
          API Keys
        </Typography>
        
        <Button
          variant="contained"
          color="primary"
          startIcon={<AddIcon />}
          onClick={() => setCreateDialogOpen(true)}
          disabled={isLoading}
        >
          Create New API Key
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {fetchError && (
        <Alert 
          severity="error" 
          sx={{ mb: 3 }}
          action={
            <Button color="inherit" size="small" onClick={handleRefresh}>
              Retry
            </Button>
          }
        >
          {fetchError}
        </Alert>
      )}

      <Alert severity="info" sx={{ mb: 3 }}>
        For security reasons, API keys are only displayed once during creation. 
        Make sure to copy and store them safely when creating a new key.
      </Alert>

      {isLoading && !apiKeys?.length ? (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
          <CircularProgress />
        </Box>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>API Key</TableCell>
                <TableCell>Created</TableCell>
                <TableCell>Last Used</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {renderApiKeysList()}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Create API Key Dialog */}
      <Dialog open={createDialogOpen} onClose={() => setCreateDialogOpen(false)}>
        <DialogTitle>Create API Key</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            API keys allow your applications to authenticate with the Loxia API.
            Provide a descriptive name to identify this key's purpose.
          </DialogContentText>
          <Alert severity="warning" sx={{ mb: 2 }}>
            <Typography variant="subtitle2">Important Security Notice</Typography>
            <Typography variant="body2">
              The API key will only be displayed once after creation. 
              Make sure to copy and store it securely before closing this dialog.
            </Typography>
          </Alert>
          <TextField
            autoFocus
            margin="dense"
            label="API Key Name"
            fullWidth
            variant="outlined"
            value={newKeyName}
            onChange={(e) => setNewKeyName(e.target.value)}
            placeholder="e.g., Production CLI Tool, Development Testing"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateDialogOpen(false)}>Cancel</Button>
          <Button 
            onClick={handleCreateKey}
            variant="contained"
            disabled={!newKeyName.trim() || isLoading}
          >
            {isLoading ? <CircularProgress size={24} /> : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete API Key Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Delete API Key</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete this API key? 
            This action cannot be undone, and any applications using this key will no longer have access.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button 
            onClick={handleDeleteKey}
            color="error"
            variant="contained"
            disabled={isLoading}
          >
            {isLoading ? <CircularProgress size={24} /> : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* New API Key Display Dialog */}
      <Dialog open={!!newKeyValue} onClose={handleNewKeyDialogClose}>
        <DialogTitle>New API Key Created</DialogTitle>
        <DialogContent>
          <Alert severity="error" sx={{ mb: 2 }}>
            <Typography variant="subtitle2">One-Time Display</Typography>
            <Typography variant="body2">
              This is the only time you will see this API key. 
              Copy it now and store it securely.
            </Typography>
          </Alert>
          
          <Box bgcolor="grey.100" p={2} borderRadius={1} mb={2}>
            <Box display="flex" alignItems="center">
              <Typography variant="body2" fontFamily="monospace" sx={{ wordBreak: 'break-all' }}>
                {showNewKey ? newKeyValue : newKeyValue ? '•'.repeat(40) : ''}
              </Typography>
              <Tooltip title={showNewKey ? "Hide key" : "Show key"}>
                <IconButton 
                  size="small" 
                  onClick={() => setShowNewKey(!showNewKey)}
                  sx={{ ml: 1 }}
                >
                  {showNewKey ? <VisibilityOffIcon fontSize="small" /> : <VisibilityIcon fontSize="small" />}
                </IconButton>
              </Tooltip>
            </Box>
          </Box>
          
          <Button
            variant="contained"
            startIcon={<CopyIcon />}
            onClick={() => newKeyValue && copyToClipboard(newKeyValue)}
            fullWidth
            color="primary"
          >
            Copy API Key
          </Button>
          
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 2, textAlign: 'center' }}>
            After closing this dialog, the key will not be displayed again.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleNewKeyDialogClose} variant="outlined">
            I've Copied the Key
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={4000}
        onClose={() => setSnackbarOpen(false)}
        message={snackbarMessage}
      />
    </Box>
  );
};

export default ApiKeysList;